/**
 * Command Gate Domain
 * Ensures only one state-changing command is in-flight at a time.
 * If the server takes >2 seconds to respond, or the player tries to send a second
 * command while one is pending, the gate blocks the new command and the page
 * top says the game is waiting — and which of the two it is waiting on: a
 * slow answer, or the command a second press found on its way.
 *
 * The gate keeps its senders' waits as well (GatedSender). A command can end
 * with no answer to read in four ways, and each ends its sender's wait here,
 * once: turned away by a second press; never sent — no connection, a rejoin
 * under way, the end lobby, the socket refusing it — which the sender says
 * first, as it says a refusal, in the player's language; lost with the
 * socket or as the end lobby begins; or abandoned as the player leaves. An
 * answer ends the wait in its domain's handler, once the router has read it
 * in shape.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { oneShotClock } from '../utils/one-shot-clock';
import { useMatchPhaseStore } from './match-phase';
import { OpCode } from '../constants/op-codes';
import { getViewerUserId } from './current-team';
import i18n from '../i18n/config';
import { matchConnection } from '../services/nakama/match-send';

// ==================== CONSTANTS ====================

const WAITING_THRESHOLD_MS = 2000;

// Replies must match the outstanding command. Failures and the explicitly listed
// pricing/help successes are delivered only to the requester by the server.
// Every request here is one a sender in the client sends through the gate, and
// every command the gate sends is one a row here answers (GatedRequest).
const GATED_RESPONSES = [
  [OpCode.OpCodeCommercialActionFailed, { request: OpCode.OpCodeCommercialAction, direct: true }],
  // A commercial action's success: the snapshot every team is sent then names the player who acted.
  [OpCode.OpCodeCommercialState, { request: OpCode.OpCodeCommercialAction, direct: false }],
  [OpCode.OpCodeAddFleetSlotSuccess, { request: OpCode.OpCodeAddFleetSlot, direct: false }],
  [OpCode.OpCodeAddFleetSlotFailed, { request: OpCode.OpCodeAddFleetSlot, direct: true }],
  [OpCode.OpCodeRemoveFleetSlotSuccess, { request: OpCode.OpCodeRemoveFleetSlot, direct: false }],
  [OpCode.OpCodeRemoveFleetSlotFailed, { request: OpCode.OpCodeRemoveFleetSlot, direct: true }],
  [OpCode.OpCodeUpdateFleetSlotSuccess, { request: OpCode.OpCodeUpdateFleetSlot, direct: false }],
  [OpCode.OpCodeUpdateFleetSlotFailed, { request: OpCode.OpCodeUpdateFleetSlot, direct: true }],
  [OpCode.OpCodePurchaseAirplanesSuccess, { request: OpCode.OpCodePurchaseAirplanes, direct: false }],
  [OpCode.OpCodePurchaseAirplanesFailed, { request: OpCode.OpCodePurchaseAirplanes, direct: true }],
  [OpCode.OpCodeSellAirplanesSuccess, { request: OpCode.OpCodeSellAirplanes, direct: false }],
  [OpCode.OpCodeSellAirplanesFailed, { request: OpCode.OpCodeSellAirplanes, direct: true }],
  [OpCode.OpCodeAddSegmentSuccess, { request: OpCode.OpCodeAddSegment, direct: false }],
  [OpCode.OpCodeAddSegmentFailed, { request: OpCode.OpCodeAddSegment, direct: true }],
  [OpCode.OpCodeUpdateFlightFrequencySuccess, { request: OpCode.OpCodeUpdateFlightFrequency, direct: false }],
  [OpCode.OpCodeUpdateFlightFrequencyFailed, { request: OpCode.OpCodeUpdateFlightFrequency, direct: true }],
  [OpCode.OpCodeRemoveFlightSuccess, { request: OpCode.OpCodeRemoveFlight, direct: false }],
  [OpCode.OpCodeRemoveFlightFailed, { request: OpCode.OpCodeRemoveFlight, direct: true }],
  [OpCode.OpCodeUpdateItineraryPriceSuccess, { request: OpCode.OpCodeUpdateItineraryPrice, direct: true }],
  [OpCode.OpCodeUpdateItineraryPriceFailed, { request: OpCode.OpCodeUpdateItineraryPrice, direct: true }],
  [OpCode.OpCodeUpdateRoutePricingPolicySuccess, { request: OpCode.OpCodeUpdateRoutePricingPolicy, direct: true }],
  [OpCode.OpCodeUpdateRoutePricingPolicyFailed, { request: OpCode.OpCodeUpdateRoutePricingPolicy, direct: true }],
  [OpCode.OpCodeResetItineraryPriceToAutoSuccess, { request: OpCode.OpCodeResetItineraryPriceToAuto, direct: true }],
  [OpCode.OpCodeResetItineraryPriceToAutoFailed, { request: OpCode.OpCodeResetItineraryPriceToAuto, direct: true }],
  [OpCode.OpCodeUnlockInnovationSuccess, { request: OpCode.OpCodeUnlockInnovation, direct: false }],
  [OpCode.OpCodeUnlockInnovationFailed, { request: OpCode.OpCodeUnlockInnovation, direct: true }],
  [OpCode.OpCodeTogglePolicySuccess, { request: OpCode.OpCodeTogglePolicy, direct: false }],
  [OpCode.OpCodeTogglePolicyFailed, { request: OpCode.OpCodeTogglePolicy, direct: true }],
  [OpCode.OpCodeCapitalDebtOrderSuccess, { request: OpCode.OpCodeCapitalDebtOrder, direct: false }],
  [OpCode.OpCodeCapitalDebtOrderFailed, { request: OpCode.OpCodeCapitalDebtOrder, direct: true }],
  [OpCode.OpCodeCapitalEquityOrderSuccess, { request: OpCode.OpCodeCapitalEquityOrder, direct: false }],
  [OpCode.OpCodeCapitalEquityOrderFailed, { request: OpCode.OpCodeCapitalEquityOrder, direct: true }],
  [OpCode.OpCodeUnlockCitySuccess, { request: OpCode.OpCodeUnlockCity, direct: false }],
  [OpCode.OpCodeUnlockCityFailed, { request: OpCode.OpCodeUnlockCity, direct: true }],
  [OpCode.OpCodeRequestHelpSuccess, { request: OpCode.OpCodeRequestHelp, direct: true }],
  [OpCode.OpCodeRequestHelpFailed, { request: OpCode.OpCodeRequestHelp, direct: true }],
  [OpCode.OpCodeCancelHelpSuccess, { request: OpCode.OpCodeCancelHelp, direct: true }],
  [OpCode.OpCodeAdminAddResourcesSuccess, { request: OpCode.OpCodeAdminAddResources, direct: false }],
  [OpCode.OpCodeAdminAddResourcesFailed, { request: OpCode.OpCodeAdminAddResources, direct: true }],
  [OpCode.OpCodeAdminJumpToNextTickSuccess, { request: OpCode.OpCodeAdminJumpToNextTick, direct: false }],
  [OpCode.OpCodeAdminJumpToNextTickFailed, { request: OpCode.OpCodeAdminJumpToNextTick, direct: true }],
  [OpCode.OpCodeAdminSetInnovationSpendLockedSuccess, { request: OpCode.OpCodeAdminSetInnovationSpendLocked, direct: false }],
  [OpCode.OpCodeAdminSetInnovationSpendLockedFailed, { request: OpCode.OpCodeAdminSetInnovationSpendLocked, direct: true }],
  [OpCode.OpCodeAdminForceEndMatchSuccess, { request: OpCode.OpCodeAdminForceEndMatch, direct: false }],
  [OpCode.OpCodeAdminForceEndMatchFailed, { request: OpCode.OpCodeAdminForceEndMatch, direct: true }],
  [OpCode.OpCodeDevSaveStressLocalSuccess, { request: OpCode.OpCodeDevSaveStressLocal, direct: true }],
  [OpCode.OpCodeDevSaveStressLocalFailed, { request: OpCode.OpCodeDevSaveStressLocal, direct: true }],
] as const;

/**
 * A command the gate sends: one its table answers. A send nothing answers
 * would hold the gate until a drop, so it does not compile.
 */
export type GatedRequest = (typeof GATED_RESPONSES)[number][1]['request'];

/** The table by the answer's op code. */
const RESPONSES = new Map<number, { request: GatedRequest; direct: boolean }>(GATED_RESPONSES);

// ==================== STORE ====================

/**
 * Why the page top says the game is waiting: the answer to the command on its
 * way is slow (WAITING_THRESHOLD_MS have passed), or the player pressed
 * something else while it was on its way, and that press went nowhere.
 */
export type CommandWait = 'slowAnswer' | 'secondPress';

interface CommandGateState {
  pendingOpCode: GatedRequest | null;
  /** What the page top's banner says, while it says anything: null while the answer is not yet due. */
  waiting: CommandWait | null;
}

const waitingClock = oneShotClock();
let pendingSend: symbol | null = null;
/** What ends the pending command's sender's own wait, should its answer be lost. */
let pendingEnd: (() => void) | null = null;

export const useCommandGateStore = create<CommandGateState>()(
  devtools(
    (): CommandGateState => ({
      pendingOpCode: null,
      waiting: null,
    }),
    { name: 'command-gate-store' },
  ),
);

/**
 * The gate opens, the command on its way let go. The gate's own: an answer
 * opens it, and the answer's handler ends the sender's wait
 * (acknowledgeGatedResponse); every other end goes through
 * abandonCommandInFlight, which ends the wait too, so nothing outside opens
 * the gate and leaves a wait that nothing ends.
 */
function clearPending(): void {
  pendingSend = null;
  pendingEnd = null;
  waitingClock.stop();
  useCommandGateStore.setState({ pendingOpCode: null, waiting: null }, false, 'clearPending');
}

// ==================== SERVICE ====================

/**
 * How a command's sender hears it ended with no answer to read. Every sender
 * hands the gate one, so every sender says a command that never went.
 */
interface GatedSender {
  /**
   * Ends the sender's own wait — a switch resting, the route builder's
   * "Adding…" — as the command ends with no answer: turned away while another
   * was on its way, or never sent (`lost` false: nothing went), or lost after
   * it went — the socket dropped, the end lobby began, the player left — when
   * the server may have carried it out (`lost` true). Called once, on each of
   * those, and after `notSent` for a command that never went. Left out by a
   * sender that keeps no wait.
   */
  endWait?: (lost: boolean) => void;
  /**
   * The command never reached the server — where nothing will answer, a
   * failure the player is not told is a press that did nothing. `refusal` is
   * shaped as the server's refusal is, its `error` the reason in the
   * player's language now (NOT_SENT_WORDS), so a sender hands it to the
   * handler that says the server's. `reason` is why, apart from it, so no
   * refusal table reads it as a server's code: a sender that keeps it to say
   * later keeps `reason`, worded in whatever language the player reads it in.
   */
  notSent: (refusal: { error: string }, reason: NotSentReason) => void;
}

/**
 * Why a command never reached the server: the match is in its end lobby,
 * which answers nothing, or the game is not connected to the match — no
 * socket, a rejoin under way, or the socket refusing the send.
 */
export type NotSentReason = 'matchOver' | 'notConnected';

/** The key that says each reason a command was not sent, in the player's language. */
export const NOT_SENT_WORDS: Record<NotSentReason, string> = {
  matchOver: 'commandGate.matchOver',
  notConnected: 'commandGate.notConnected',
};

/**
 * The answer to the command on its way opens the gate. The router calls this
 * once the message is in the shape its handler reads, so an answer out of
 * shape — whose handler, which ends the sender's wait, does not run — opens
 * nothing.
 */
export function acknowledgeGatedResponse(opCode: number, data?: unknown): void {
  if (answersCommandOnItsWay(opCode, data)) clearPending();
}

/**
 * An answer to the command on its way that is not in the shape its handler
 * reads, so the handler — which ends the sender's wait — will not run. The
 * command ends as one whose answer was lost (abandonCommandInFlight): the
 * gate opens and the wait ends, rather than the gate shut for good, or open
 * with the wait never ended. The router calls it as a guard turns a message
 * away.
 */
export function abandonUnreadableResponse(opCode: number, data: unknown): void {
  if (answersCommandOnItsWay(opCode, data)) abandonCommandInFlight();
}

/** Whether a message is the answer to the command on its way: its request's, and to this player. */
function answersCommandOnItsWay(opCode: number, data: unknown): boolean {
  const response = RESPONSES.get(opCode);
  if (!response || useCommandGateStore.getState().pendingOpCode !== response.request) return false;
  const sender = data && typeof data === 'object' && 'sender_user_id' in data
    ? data.sender_user_id : undefined;
  const userId = getViewerUserId();
  // Broadcast successes require the acting player's identity. Direct responses
  // may omit it, but an explicitly different sender is never our acknowledgment.
  return sender !== undefined ? !!userId && sender === userId : response.direct;
}

/**
 * A command the match will not answer: it is in its end lobby, where the
 * server reads no player's command (match-handler.go), so a send would hold
 * the gate until a reconnect.
 */
class MatchOver extends Error {
  constructor() {
    super('The match has ended: the end lobby answers no command.');
    this.name = 'MatchOver';
  }
}

/**
 * Why a send failed here, before the server saw it — with no connection to
 * the match, or in the end lobby, which answers nothing: the reason every
 * sender's notSent hears, in place of the socket's English. The error itself
 * goes to the console.
 */
function notSentReason(error: unknown): NotSentReason {
  console.error('[CommandGate] Send failed:', error);
  return error instanceof MatchOver ? 'matchOver' : 'notConnected';
}

/**
 * The command on its way will not be answered: the socket dropped with it,
 * the match entered its end lobby, which answers no command, or the player
 * left. The command is over: the gate opens and the sender's own wait for the
 * answer ends (its endWait, lost), so the player can act again once the game
 * has rejoined. Nothing is sent again by itself: the server may have carried
 * the command out, and the rejoin's snapshot says what stands. Run as play
 * ends (endPlay, end-play.ts) — on every unexpected drop, as the player
 * leaves, and as a notice says play is over — and as the end lobby begins
 * (match-phase.ts).
 */
export function abandonCommandInFlight(): void {
  const endWait = pendingEnd;
  clearPending();
  try {
    endWait?.();
  } catch (error) {
    // A sender's end failing costs that wait alone, never the reconnect
    // that follows the drop.
    console.error('[CommandGate] Ending the lost command\'s wait failed:', error);
  }
}

/**
 * Closes the gate on a command about to go out: it holds for that command's
 * answer, keeps `onLost` for its answer's loss to call
 * (abandonCommandInFlight), and says the game is waiting if no answer has
 * come in WAITING_THRESHOLD_MS. Returns the send's token, which a failed send
 * checks before it opens the gate, so it never opens one a newer command
 * holds. Only with the gate open: closed again, it would drop the first
 * command's `onLost`, which is why sendGatedCommand checks first. It closes
 * the gate just before the socket takes the command; the UI gallery's
 * command-gate scene closes it with no socket to send through, to show the
 * gate waiting.
 */
export function holdGate(opCode: GatedRequest, onLost: (() => void) | null): symbol {
  waitingClock.stop();
  const sendToken = Symbol();
  pendingSend = sendToken;
  pendingEnd = onLost;

  useCommandGateStore.setState(
    { pendingOpCode: opCode, waiting: null },
    false,
    'holdGate',
  );

  // Slow: the banner says so — unless a second press has said why already.
  waitingClock.start(WAITING_THRESHOLD_MS, () => {
    const current = useCommandGateStore.getState();
    if (current.pendingOpCode !== null) {
      useCommandGateStore.setState({ waiting: current.waiting ?? 'slowAnswer' }, false, 'waitingTimeout');
    }
  });
  return sendToken;
}

/**
 * Sends a state-changing command through the gate. Resolves true once it has
 * gone, and false when it did not, never rejecting: a second press while a
 * command is on its way is turned away (the page top says why), and a command
 * that cannot reach the server — the end lobby, which answers nothing; no
 * socket or match; a rejoin under way, whose socket is closed, or new and not
 * back in the match yet, where the server would ignore it; the socket
 * refusing it — is said through the sender's notSent. Either way the sender's
 * wait ends here (GatedSender), as it does when the answer is lost after the
 * command went.
 */
export async function sendGatedCommand(opCode: GatedRequest, data: string, sender: GatedSender): Promise<boolean> {
  const notSent = (error: unknown): false => {
    const reason = notSentReason(error);
    sender.notSent({ error: i18n.t(NOT_SENT_WORDS[reason]) }, reason);
    sender.endWait?.(false);
    return false;
  };

  // The end lobby answers no command: said at once, whatever is on its way.
  if (useMatchPhaseStore.getState().phase === 'end_lobby') return notSent(new MatchOver());

  const store = useCommandGateStore.getState();

  // A second press while a command is on its way goes nowhere, and the page
  // top says why at once. A drop ends the command on its way first
  // (abandonCommandInFlight), so the gate is open whenever the socket is gone.
  if (store.pendingOpCode !== null) {
    console.warn(
      '[CommandGate] Blocked opcode', opCode,
      '— still waiting for response to opcode', store.pendingOpCode,
    );
    useCommandGateStore.setState({ waiting: 'secondPress' }, false, 'blocked');
    sender.endWait?.(false);
    return false;
  }

  const connection = matchConnection();
  if (!connection) return notSent(new Error('Not connected to match.'));

  const sendToken = holdGate(opCode, sender.endWait ? () => sender.endWait?.(true) : null);

  try {
    await connection.socket.sendMatchState(connection.matchId, opCode, data);
  } catch (error) {
    // A failed send has no answer coming. The gate opens, unless it has let
    // this command go already — lost with the socket, or answered — when its
    // end has been said, and a newer command may hold the gate.
    if (pendingSend !== sendToken) return false;
    clearPending();
    return notSent(error);
  }
  return true;
}
